import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import styled from 'styled-components';

import colors from 'styleguide/colors';
import { iconSizes } from 'styleguide/constants';

/**
 * Given a `selectFunction` that returns a boolean, returns one or the other component
 */
export const selectComponent = (selectFunction) => (TrueComponent, FalseComponent) => (props) => {
  if (selectFunction(props)) {
    return <TrueComponent {...props} />;
  }
  return <FalseComponent {...props} />;
};

const iconHover = ({ isHoverable }) => {
  if (isHoverable) {
    return `
      opacity: 0.5;
      cursor pointer;
    `;
  }
  return null;
};

const IconWrapper = styled.span`
  &:hover {
    ${iconHover}
  }
`;

const iconShell = (
  WrappedComponent,
  { defaultBackgroundColor = 'primary', defaultColor = 'neutral' } = {},
) =>
  // eslint-disable-next-line
  React.forwardRef(({ size, color, shade, backgroundColor, raw, ...otherProps }, ref) => {
    const component = (
      <WrappedComponent
        width={size ? iconSizes[size] || iconSizes.md : iconSizes.md}
        color={color === 'inherit' ? 'inherit' : colors(color || defaultColor, shade || 0)}
        backgroundColor={colors(backgroundColor || defaultBackgroundColor)}
        {...otherProps}
      />
    );
    // TODO(michele): find a better way to do this.
    if (raw) {
      return component;
    }
    return (
      <IconWrapper ref={ref} {...otherProps}>
        {component}
      </IconWrapper>
    );
  });

export const icon = (Wrapped, props) => iconShell(Wrapped, props);

/**
 * Allow to easily toggle a component.
 * Retuns a new component that will have a `isOpen` and `toggle` props to allow toggling the component.
 * Reduces a lot of the boilerplate in creating modals and dropdowns
 */
export const toggleComponent = (WrappedComponent) => {
  class ToggleComponent extends React.Component {
    static WrappedComponent = WrappedComponent;

    constructor(props) {
      super(props);

      this.state = { isOpen: false };
    }

    toggle() {
      this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
    }

    render() {
      const { isOpen } = this.state;
      return <WrappedComponent isOpen={isOpen} toggle={() => this.toggle()} {...this.props} />;
    }
  }

  ToggleComponent.displayName = `withToggle(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return hoistNonReactStatics(ToggleComponent, WrappedComponent);
};
